import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import RecipeForm from '../components/recipes/RecipeForm';
import { RecipeContext } from '../contexts/RecipeContext';
import { Recipe } from '../types/Recipe';

const EditRecipePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getRecipeById, updateExistingRecipe } = useContext(RecipeContext);
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (id) {
      const recipeData = getRecipeById(id);
      if (recipeData) {
        setRecipe(recipeData);
      }
      setLoading(false);
    }
  }, [id, getRecipeById]);

  const handleSubmit = (recipeData: any) => {
    if (id && recipe) {
      updateExistingRecipe(id, {
        ...recipeData,
        updatedAt: new Date()
      });
      
      // Afficher un message de succès
      alert('Recette modifiée avec succès !');
      
      // Rediriger vers la page de détail de la recette
      navigate(`/recipe/${id}`);
    }
  };

  const handleCancel = () => {
    if (id) {
      navigate(`/recipe/${id}`);
    } else {
      navigate('/recipes');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow py-20 bg-light flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow py-20 bg-light">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl font-bold mb-6" data-aos="fade-up">Recette non trouvée</h1>
            <p className="text-gray-600 mb-8" data-aos="fade-up" data-aos-delay="100">
              La recette que vous souhaitez modifier n'existe pas ou a été supprimée.
            </p>
            <button 
              onClick={() => navigate('/recipes')}
              className="bg-gradient-to-r from-primary to-secondary text-white font-medium py-2 px-6 rounded-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
              data-aos="fade-up" data-aos-delay="200"
            >
              Retourner à mes recettes
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow py-10 md:py-20 bg-light">
        <div className="container mx-auto px-4">
          <div className="mb-6 flex items-center" data-aos="fade-in">
            <button 
              onClick={handleCancel}
              className="flex items-center text-gray-600 hover:text-primary transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              Annuler les modifications
            </button>
          </div>
          
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold mb-4" data-aos="fade-up">Modifier la recette</h1>
            <p className="text-gray-600 max-w-2xl mx-auto" data-aos="fade-up" data-aos-delay="100">
              Modifiez les informations de votre recette et enregistrez vos changements.
            </p>
          </div>
          
          <div data-aos="fade-up" data-aos-delay="200">
            <RecipeForm 
              onSubmit={handleSubmit} 
              initialData={recipe}
              isEditing={true}
            />
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default EditRecipePage;
